// tslint:disable:no-trailing-whitespace
// tslint:disable:member-ordering
import { BaseEntity } from '../base-entity';
import { CancellationRequestDetail } from './cancellation-request-detail';
import { InvoiceDetail } from './invoice-detail';
import { ItemDetail } from './item-detail';
import { ItemDetailAddon } from './item-detail-addon';
import { JoDetailAddon } from './jo-detail-addon';
import { JoHeader } from './jo-header';
import { JoNote } from './jo-note';
import { PoDetail } from './po-detail';
import { Product } from './product';
import { ReturnRequestDetail } from './return-request-detail';

/// <code-import> Place custom imports between <code-import> tags
import _ from "lodash";
import { EntityPropertyAnnotation, EntityTypeAnnotation } from "../entity-type-annotation";
import { CancellationRequestDetailStatusEnum, ItemDetailStatusEnum, ReturnRequestDetailStatusEnum } from "./enums";
/// </code-import>

/// <module-code> Place module level code between <module-code> tags

/// </module-code>

export class JoDetail extends BaseEntity  {

  /// <code> Place custom code between <code> tags
  static getEntityTypeAnnotation(): EntityTypeAnnotation {
    const propAnnotations = [
      new EntityPropertyAnnotation("orderQty", {
        validators: [BaseEntity.greaterThanZeroValidator],
      }),
      new EntityPropertyAnnotation("unitPriceAmt", {
        displayName: "Price/Unit",
        validators: [BaseEntity.greaterThanZeroValidator],
      }),
    ];

    return new EntityTypeAnnotation({
      validators: [],
      propertyAnnotations: propAnnotations,
    });
  }

  // conceptual Note interface
  getHeader() {
    return this.joHeader;
  }

  getNotes() {
    return this.joNotes;
  }

  isAccountOwned() {
    return this.joHeader.isAccountOwnedOrder;
  }
  //

  // getItemDetailsAppliedQty() was renamed getItemDetailsRcvdQty

  getItemDetailsRcvdQty() {
    // count of itemDetails marked with for this jod
    const r = this.itemDetails && this.itemDetails.filter((itd) => itd.itemDetailStatusId <= ItemDetailStatusEnum.Shipped).length;
    return r;
  }

  getAccountPurchaseItemDetailsQty() {
    return this.accountPurchaseItemDetails?.length || 0;
  }

  getItemDetailsVoucheredQty() {
    // count of itemDetails marked with for this jod
    const r = this.itemDetails && this.itemDetails.filter((itd) => itd.itemDetailStatusId == ItemDetailStatusEnum.InProcessVoucher).length;
    return r;
  }

  getItemDetailsShippedQty() {
    // count of itemDetails marked with for this jod
    const r = this.itemDetails && this.itemDetails.filter((itd) => itd.itemDetailStatusId === ItemDetailStatusEnum.Shipped).length;
    return r;
  }

  getItemDetailsDamagedQty() {
    return this.itemDetails && this.itemDetails.filter((itd) => itd.itemDetailStatusId === ItemDetailStatusEnum.DamagedOrOther).length;
  }

  getUnshippedQty() {
    return this.orderQty - this.cancelQty - this.getItemDetailsShippedQty();
  }

  getShippedAndVoucheredQty() {
    const r = this.getItemDetailsShippedQty() + this.getItemDetailsVoucheredQty();
    return r;
  }

  getUnshippedAndUnvoucheredQty() {
    return this.orderQty - this.cancelQty - this.getShippedAndVoucheredQty() - this.getAccountPurchaseItemDetailsQty();
  }

  // Note - next 2 methods require poDetails, poDetails.poDetailCancels and poDetails.itemDetails

  // This value should be 0 for all Open Complete JO's
  getUnresolvedQty() {
    // Ignore returnQty for this purpose
    //          Ordered                             Already pulled/rcvd                Open PO                        Held
    return this.orderQty - (this.cancelQty + this.getItemDetailsRcvdQty() + this.getAccountPurchaseItemDetailsQty() + this.getPoDetailsRemainingQty() + this.getPoDetailsHeldQty());
  }

  getPoDetailsRemainingQty() {
    const r = this.poDetails && _.sum(this.poDetails.map((pod) => pod.getRemainingQty()));
    return r;
  }

  getPoDetailsRemainingUnshippedQty() {
    return this.poDetails && _.sum(this.poDetails.map((pod) => pod.getRemainingUnshippedQty()));
  }

  getPoDetailsHeldQty() {
    const r = this.poDetails && _.sum(this.poDetails.map((pod) => pod.heldQty));
    return r;
  }

  get calcExpectedShipDate() {
    if (!this.joHeader) return null;
    if (!this.expectedShipDate) return this.joHeader.getJoShipDate();
    return this.expectedShipDate;
  }

  // requires all mods to have been queried
  // should be the same as UnitCustomAmt
  get unitModsAmt() {
    return _.sum(this.joDetailAddons.map((jodemb) => jodemb.unitPriceAmt ?? 0));
  }

  get unitTotalAmt() {
    return (this.unitPriceAmt || 0) + (this.unitCustomAmt || 0) + (this.unitOversizeAmt || 0);
  }

  /**
   * Sum of accepted cancellation-request qty for this JoDetail.
   *
   * Callers MUST load JoDetail with `.expand('cancellationRequestDetails')`
   * or this returns 0.
   */
  get cancelQty(): number {
    return _.sumBy(
      (this.cancellationRequestDetails ?? []).filter(
        c => c.cancellationRequestDetailStatusId === CancellationRequestDetailStatusEnum.Accepted
      ),
      'qty'
    );
  }

  /**
   * Sum of return-request qty for this JoDetail, excluding awaiting-receipt
   * and rejected-back-to-customer statuses.
   *
   * Callers MUST load JoDetail with `.expand('returnRequestDetails')`
   * or this returns 0.
   */
  get returnQty(): number {
    return _.sumBy(
      (this.returnRequestDetails ?? []).filter(
        r => r.returnRequestDetailStatusId !== ReturnRequestDetailStatusEnum.AwaitingReceipt
          && r.returnRequestDetailStatusId !== ReturnRequestDetailStatusEnum.ReturnedToCustomerRejected
      ),
      'qty'
    );
  }
  /// </code>

  // Generated code. Do not place code below this line.
  /** Int32 key */
  id!: number;
  /** DateTime */
  expectedShipDate?: Date;
  /** Int32 FK joHeader */
  joHeaderId!: number;
  /** Int32 */
  orderQty!: number;
  /** Int32 FK product */
  productId!: number;
  /** String(60) */
  productTypeConfigName!: string;
  /** Guid */
  proximityJoDetailId?: string;
  /** Binary */
  rowVersion!: any;
  /** Decimal */
  unitCustomAmt!: number;
  /** Decimal */
  unitOversizeAmt!: number;
  /** Decimal */
  unitPriceAmt!: number;
  /** Decimal */
  unitReissueAmt!: number;
  /** Decimal */
  unitRestockAmt!: number;
  /** Inv FK accountPurchaseJoDetailId */
  accountPurchaseItemDetails!: ItemDetail[];
  /** Inv FK joDetailId */
  cancellationRequestDetails!: CancellationRequestDetail[];
  /** Inv FK joDetailId */
  invoiceDetails!: InvoiceDetail[];
  /** Inv FK supersededByJoDetailId */
  itemDetailAddons!: ItemDetailAddon[];
  /** Inv FK joDetailId */
  itemDetails!: ItemDetail[];
  /** Inv FK joDetailId */
  joDetailAddons!: JoDetailAddon[];
  /** FK joHeaderId */
  joHeader!: JoHeader;
  /** Inv FK joDetailId */
  joNotes!: JoNote[];
  /** Inv FK joDetailId */
  poDetails!: PoDetail[];
  /** FK productId */
  product!: Product;
  /** Inv FK joDetailId */
  returnRequestDetails!: ReturnRequestDetail[];
}

